import type {FsSimpleData} from '@/services/auditService/auditAdjustmentService';
import {
    AI_APPENDIX_ENTRIES,
    AI_INDEX_ENTRIES,
    BS_DRIVEN_INDEX_LETTERS,
    BS_PL_INDEX_LETTERS,
} from '@/pages/components/audit-program/ai-section/constants';

function flattenLeaves(rows: FsSimpleData[]): FsSimpleData[] {
    const out: FsSimpleData[] = [];
    const walk = (nodes: FsSimpleData[]) => {
        for (const node of nodes) {
            if (node.children?.length) {
                walk(node.children);
            } else {
                out.push(node);
            }
        }
    };
    walk(rows);
    return out;
}

function hasPresence(row: FsSimpleData): boolean {
    const opening = Math.abs(Number(row.openingAmount ?? 0));
    const final = Math.abs(Number(row.finalAmount ?? 0));
    const lastYear = Math.abs(Number(row.lastYearAudited ?? 0));
    if (opening > 0 || final > 0 || lastYear > 0) return true;
    if (row.wpStatus != null) return true;
    if (row.adjustByPaper) return true;
    if (Array.isArray(row.adjustment) && row.adjustment.some((a) => Math.abs(Number(a.adjustment ?? 0)) > 0)) {
        return true;
    }
    return false;
}

function isBsRow(code: string): boolean {
    const c = (code || '').trim().toUpperCase();
    return c.startsWith('BS-') || c === 'FS-BS';
}

function isPlRow(code: string): boolean {
    const c = (code || '').trim().toUpperCase();
    return c.startsWith('PL-') || c === 'FS-PL';
}

/**
 * Map FS schedule_name (+ fs code) to Ai index letter.
 * Aligns with account_fs_category.schedule_name and C7 materiality sections.
 */
export function scheduleToAiLetter(scheduleName: string, fsCode: string): string | null {
    const sn = (scheduleName || '').trim().toUpperCase();
    const code = (fsCode || '').trim().toUpperCase();
    if (!sn || sn === 'NONE') return null;

    if (sn.startsWith('LX')) return 'M';
    if (sn === 'G4' || code === 'BS-CA-IOT') return 'H';
    if (sn.startsWith('G')) return 'G';
    if (sn.startsWith('QI') || sn.startsWith('QE')) return 'Q';
    if (sn.startsWith('RX') || (sn.startsWith('R') && sn.length > 1)) return 'R';

    const letter = sn[0];
    if (BS_PL_INDEX_LETTERS.has(letter)) return letter;
    return null;
}

/** Map a BS line to an index letter (E–P). PL-only schedules on BS rows are ignored. */
export function letterFromBsRow(row: FsSimpleData): string | null {
    if (!isBsRow(row.code)) return null;
    const letter = scheduleToAiLetter(row.scheduleName, row.code);
    if (!letter || !BS_DRIVEN_INDEX_LETTERS.has(letter)) return null;
    return letter;
}

/** Map a PL line to an index letter (Q/R only — not E/F/I from depreciation/amortisation). */
export function letterFromPlRow(row: FsSimpleData): string | null {
    if (!isPlRow(row.code)) return null;
    const sn = (row.scheduleName || '').trim().toUpperCase();
    if (!sn || sn === 'NONE') return null;
    if (sn.startsWith('QI') || sn.startsWith('QE')) return 'Q';
    if (sn.startsWith('RX') || (sn.startsWith('R') && sn.length > 1)) return 'R';
    return null;
}

/** Appendix rows keyed by schedule prefix AP1…AP6 when present in FS tree. */
function scheduleToAppendixCode(scheduleName: string): string | null {
    const sn = (scheduleName || '').trim().toUpperCase();
    const match = /^AP(\d)/.exec(sn);
    return match ? match[1] : null;
}

export interface DeriveActiveIndexResult {
    activeLetters: Set<string>;
    activeAppendices: Set<string>;
    /** Any PL leaf with data — ensures R (Profit and Loss) shows. */
    hasPlData: boolean;
}

export function deriveActiveIndexFromFs(
    bsLine: FsSimpleData[],
    plLine: FsSimpleData[],
): DeriveActiveIndexResult {
    const activeLetters = new Set<string>();
    const activeAppendices = new Set<string>();
    let hasPlData = false;

    for (const row of flattenLeaves(bsLine)) {
        if (!hasPresence(row)) continue;

        const letter = letterFromBsRow(row);
        if (letter) activeLetters.add(letter);

        const appendix = scheduleToAppendixCode(row.scheduleName);
        if (appendix) activeAppendices.add(appendix);
    }

    for (const row of flattenLeaves(plLine)) {
        if (!hasPresence(row)) continue;

        if (isPlRow(row.code)) hasPlData = true;

        const letter = letterFromPlRow(row);
        if (letter) activeLetters.add(letter);

        const appendix = scheduleToAppendixCode(row.scheduleName);
        if (appendix) activeAppendices.add(appendix);
    }

    if (hasPlData) activeLetters.add('R');

    return {activeLetters, activeAppendices, hasPlData};
}

export function isAiIndexEntryVisible(
    entry: {code: string; kind: 'letter' | 'appendix'; alwaysVisible?: boolean; neverVisible?: boolean},
    active: DeriveActiveIndexResult,
): boolean {
    if (entry.neverVisible) return false;
    if (entry.alwaysVisible) return true;

    if (entry.kind === 'appendix') {
        return active.activeAppendices.has(entry.code);
    }

    if (BS_PL_INDEX_LETTERS.has(entry.code) || entry.code === 'D' || entry.code === 'S'
        || ['V', 'W', 'X', 'Y', 'Z'].includes(entry.code)) {
        return active.activeLetters.has(entry.code);
    }

    return false;
}

export function buildVisibleAiIndex(active: DeriveActiveIndexResult) {
    return {
        letters: AI_INDEX_ENTRIES.filter((e) => isAiIndexEntryVisible(e, active)),
        appendices: AI_APPENDIX_ENTRIES.filter((e) => isAiIndexEntryVisible(e, active)),
    };
}
